import {
  Band,
  GroupShell,
  Money,
  PercentCaption,
  Skeleton,
  Text,
} from '@billda/design-system';
import { formatCurrency } from '../../../shared/utils/currency';
import { BAND_PERCENT_HIDDEN_BELOW, SITE_LEDGER_COLLAPSE } from './columns';
import { ItemLedgerRows, VariationLedgerEntry } from './SiteLedgerRows';
import type { SiteLedgerRowContext } from './SiteLedgerRows';
import type { PlotLedgerGroup } from './toPlotLedgerGroups';

export interface PlotLedgerBandProps {
  group: PlotLedgerGroup;
  showValues: boolean;
  canCancelBookings: boolean;
  onToggle: () => void;
  onOpenPanel: (type: string, id: string, plotId?: string) => void;
  onAdminCancel: (allocationId: string, plotId?: string) => void;
}

/**
 * A plot with nothing booked shows its target rather than a bold total: a
 * freshly imported plot is the price book, not a problem.
 */
function BandTotal({ group }: { group: PlotLedgerGroup }) {
  if (group.isTemplate) {
    return (
      <Text variant="caption" color="secondary" className="tabular-nums">
        {group.totalTarget != null
          ? `${formatCurrency(group.totalTarget)} target`
          : ''}
      </Text>
    );
  }
  if (group.totalSpent == null) {
    return null;
  }
  return <Money value={group.totalSpent} />;
}

function PlotRowsSkeleton() {
  return (
    <div className="px-4 py-2">
      {[1, 2, 3].map(row => (
        <Skeleton key={row} variant="text" height={28} />
      ))}
    </div>
  );
}

export function PlotLedgerBand({
  group,
  showValues,
  canCancelBookings,
  onToggle,
  onOpenPanel,
  onAdminCancel,
}: PlotLedgerBandProps) {
  const ctx: SiteLedgerRowContext = {
    plotId: group.plotId.toString(),
    showValues,
    canCancelBookings,
    onOpenPanel,
    onAdminCancel,
  };

  return (
    <GroupShell
      open={group.isExpanded}
      onToggle={onToggle}
      header={open => (
        <Band
          collapse={SITE_LEDGER_COLLAPSE}
          primary={group.label}
          secondary={group.secondaryLabel ?? undefined}
          count={group.itemCount ?? undefined}
          inline={
            group.isTemplate || group.progress == null ? undefined : (
              <PercentCaption percent={Math.round(group.progress)} />
            )
          }
          inlineHiddenBelow={BAND_PERCENT_HIDDEN_BELOW}
          open={open}
          onToggle={onToggle}
          onLabelClick={() => onOpenPanel('plot', group.plotId.toString())}
          showValues={showValues}
          trailing={<BandTotal group={group} />}
          data-testid="plot-band"
        />
      )}
    >
      {group.isLoading ? (
        <PlotRowsSkeleton />
      ) : (
        group.entries.map(entry =>
          entry.kind === 'item' ? (
            <ItemLedgerRows key={entry.key} entry={entry} ctx={ctx} />
          ) : (
            <VariationLedgerEntry key={entry.key} entry={entry} ctx={ctx} />
          )
        )
      )}
    </GroupShell>
  );
}
